// ===========================================
// Link an existing Meet & Greet to an enquiry (enquiries plan §8.2, §5.7)
// ===========================================
// Lists the school's Meet & Greets that are still live (not yet converted, rejected or cancelled)
// and not already linked to an enquiry (`enquiryId`, which the admin list sends with every row).
// It reads the whole Meet & Greet list, so a parent mounts it ONLY while it is open (review 5.7 M1).

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Enquiry } from '../../../api/enquiry.api';
import type { MeetAndGreet } from '../../../api/meetAndGreet.api';
import { useMeetAndGreets } from '../../../hooks/useMeetAndGreet';
import { useLinkMeetAndGreet } from '../../../hooks/useEnquiries';
import { usePermissions } from '../../../hooks/usePermissions';

export interface LinkMeetAndGreetDialogProps {
  enquiry: Enquiry;
  open: boolean;
  onClose: () => void;
  onLinked?: () => void;
}

const LINKABLE = new Set(['PENDING_VERIFICATION', 'PENDING_APPROVAL', 'APPROVED', 'WAITLISTED']);

const STATUS_LABELS: Record<string, string> = {
  PENDING_VERIFICATION: 'Awaiting verification',
  PENDING_APPROVAL: 'Awaiting approval',
  APPROVED: 'Approved',
  WAITLISTED: 'Waitlisted',
};

export default function LinkMeetAndGreetDialog({ enquiry, open, onClose, onLinked }: LinkMeetAndGreetDialogProps) {
  // The list is `GET /admin/meet-and-greet` (`meet_and_greet.read`): asked only when it may be read
  // (#272; whole-phase review M1).
  const mayList = usePermissions().can('meet_and_greet', 'read');
  const { data, isLoading: loadingList, isError } = useMeetAndGreets(undefined, open && mayList);
  const isLoading = mayList && loadingList;
  const link = useLinkMeetAndGreet();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Start over each time it opens (review 5.7 L14).
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedId(null);
  }, [open, enquiry.id]);

  const candidates = useMemo(() => {
    const rows: MeetAndGreet[] = Array.isArray(data) ? data : [];
    const term = search.trim().toLowerCase();
    return rows.filter((m) => {
      if (!LINKABLE.has(m.status) || m.enquiryId) return false;
      if (!term) return true;
      return [m.studentFirstName, m.studentLastName, m.contact1Name, m.contact1Email, m.contact2Name, m.contact2Email]
        .filter((v): v is string => !!v)
        .some((v) => v.toLowerCase().includes(term));
    });
  }, [data, search]);

  // Only a row that is on screen can be linked.
  const selected = candidates.find((m) => m.id === selectedId) ?? null;

  const handleLink = () => {
    if (!selected) return;
    link.mutate(
      { meetAndGreetId: selected.id, enquiryId: enquiry.id },
      {
        onSuccess: () => {
          setSelectedId(null);
          onLinked?.();
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={link.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link an existing Meet & Greet</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            size="small"
            fullWidth
            label="Search by student, contact or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isLoading && (
            <Stack spacing={1}>
              <Skeleton variant="rectangular" height={48} />
              <Skeleton variant="rectangular" height={48} />
            </Stack>
          )}
          {!mayList && (
            <Alert severity="info">Choosing a booking needs access to view Meet & Greets.</Alert>
          )}
          {isError && <Alert severity="error">Failed to load the Meet & Greets.</Alert>}
          {mayList && !isLoading && !isError && candidates.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No unlinked Meet & Greets{search.trim() ? ' match that search' : ' to link'}.
            </Typography>
          )}
          {!isLoading && !isError && candidates.length > 0 && (
            <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
              <List dense disablePadding>
                {candidates.map((m) => (
                  <ListItemButton key={m.id} selected={m.id === selectedId} onClick={() => setSelectedId(m.id)}>
                    <ListItemText
                      primary={`${m.studentFirstName} ${m.studentLastName}`}
                      secondary={`${m.contact1Name} · ${m.contact1Email} · ${STATUS_LABELS[m.status] ?? m.status}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={link.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleLink} disabled={!selected || link.isPending}>
          {link.isPending ? 'Linking...' : 'Link Meet & Greet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
